import { useState } from 'react';
import { Stream } from '@cloudflare/stream-react';

/**
 * Simple media gallery: shows a main media (image/video/Stream) and thumbnails below.
 * Props:
 * - items: Array<{ type: 'image'|'video', source: string, thumbnail: string, streamId?: string }>
 * - initialIndex?: number
 * - variant?: 'default' | 'square' // default keeps legacy inline styles, square relies on CSS
 */
export default function MediaGallery({ items = [], initialIndex = 0, variant = 'default' }) {
  const safeItems = Array.isArray(items) ? items : [];
  const [current, setCurrent] = useState(
    Math.min(Math.max(initialIndex, 0), Math.max(safeItems.length - 1, 0))
  );

  const currentItem = safeItems[current];
  const isSquareVariant = variant !== 'default';

  const mainStyle = isSquareVariant ? undefined : { width: '100%', display: 'block' };
  const thumbsWrapStyle = isSquareVariant
    ? undefined
    : { display: 'flex', gap: 10, marginTop: 12, overflowX: 'auto' };
  const thumbStyle = (active) =>
    isSquareVariant
      ? undefined
      : {
          width: 90,
          height: 70,
          flex: '0 0 auto',
          border: active ? '3px solid #111827' : '2px solid #e5e7eb',
          borderRadius: 6,
          cursor: 'pointer',
          overflow: 'hidden',
          background: '#f9fafb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'border-color 0.2s ease',
        };

  const renderMain = () => {
    if (!currentItem) return null;
    if (currentItem.type === 'image') {
      return (
        <img src={currentItem.source} alt='' className='product-info-gallery__media' style={mainStyle} />
      );
    }
    if (currentItem.streamId) {
      return (
        <div className='product-info-gallery__media product-info-gallery__media--stream' style={mainStyle}>
          <Stream
            src={currentItem.streamId}
            controls
            autoplay
            muted
            poster={currentItem.thumbnail || currentItem.source}
          />
        </div>
      );
    }
    return (
      <video
        playsInline
        autoPlay
        muted
        poster={currentItem.thumbnail || currentItem.source}
        controls
        src={currentItem.source}
        className='product-info-gallery__media'
        style={mainStyle}
      />
    );
  };

  return (
    <div className='product-right-images'>
      <div className='product-info-gallery js-gallery'>
        <div className='product-info-gallery__img'>{renderMain()}</div>
      </div>

      <div className='product-info-gallery-thumbs js-thumbs' style={thumbsWrapStyle}>
        {safeItems.map((item, idx) => (
          <button
            key={`${item.thumbnail || item.source}-${idx}`}
            className={`product-info-gallery-thumbs__img${idx === current ? ' is-active' : ''}`}
            onClick={() => setCurrent(idx)}
            type='button'
            aria-label={`Select media ${idx + 1}`}
            aria-pressed={idx === current}
            style={thumbStyle(idx === current)}
          >
            <img
              src={item.thumbnail || item.source}
              alt='thumbnail'
              className='product-info-gallery-thumbs__media'
            />
          </button>
        ))}
      </div>
    </div>
  );
}
